import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search as SearchIcon, TrendingUp, Boxes, Clock, AlertTriangle, type LucideIcon } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { Table } from '@/components/ui/Table';
import { ItemThumb } from '@/components/ui/ItemThumb';
import { KpiCard } from '@/components/ui/KpiCard';
import { Pagination } from '@/components/ui/Pagination';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { SearchableSelect, type SearchableOption } from '@/components/form/SearchableSelect';
import { getSisAging, type SisAgingRow } from '@/api/sisAging';
import { listSisStores } from '@/api/sisStock';

/** Aging-bucket accent colours: fresher = green, older = red. */
type BucketKey = 'd0_30' | 'd31_60' | 'd61_90' | 'd90plus';
const BUCKETS: { key: BucketKey; label: string; accent: string; Icon: LucideIcon }[] = [
  { key: 'd0_30', label: '0–30 days', accent: '#10b981', Icon: Clock },
  { key: 'd31_60', label: '31–60 days', accent: '#f59e0b', Icon: Clock },
  { key: 'd61_90', label: '61–90 days', accent: '#f97316', Icon: Clock },
  { key: 'd90plus', label: '90+ days', accent: '#ef4444', Icon: AlertTriangle },
];

export function SisAgingPage() {
  const [group, setGroup] = useState('');
  const [partyId, setPartyId] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const filter = useMemo(
    () => ({ group: group || undefined, partyId: partyId ? Number(partyId) : undefined }),
    [group, partyId],
  );
  const storesQ = useQuery({ queryKey: ['sis-stores'], queryFn: () => listSisStores(), staleTime: 0 });
  const query = useQuery({
    queryKey: ['sis-aging', filter],
    queryFn: () => getSisAging(filter),
    refetchInterval: 60_000,
  });
  const data = query.data;
  const summary = data?.summary;

  const groups = useMemo(
    () => Array.from(new Set((storesQ.data ?? []).map((s) => s.group).filter(Boolean))).sort(),
    [storesQ.data],
  );
  const storeOptions = useMemo<SearchableOption[]>(
    () => [
      { value: '', label: 'All Stores' },
      ...(storesQ.data ?? []).map((s) => ({ value: String(s.id), label: s.name, sublabel: s.city || undefined })),
    ],
    [storesQ.data],
  );

  const filtered = useMemo(() => {
    const rows = data?.rows ?? [];
    const terms = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!terms.length) return rows;
    return rows.filter((r) => {
      const hay = `${r.storeName} ${r.group} ${r.itemCode} ${r.itemBarcode} ${r.itemName}`.toLowerCase();
      return terms.every((t) => hay.includes(t));
    });
  }, [data, search]);

  useEffect(() => { setPage(1); }, [search, group, partyId]);
  const total = filtered.length;
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div>
      <SectionHeader
        title="Sell-through & Aging"      />

      {/* Summary */}
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <KpiCard label="Sell-through %" value={summary ? `${summary.sellThroughPct}%` : '—'} accent="#8b5cf6" Icon={TrendingUp} loading={query.isFetching && !data} />
        <KpiCard label="Units On Hand" value={summary ? summary.onHand : '—'} accent="#3b82f6" Icon={Boxes} loading={query.isFetching && !data} />
        {BUCKETS.map((b) => (
          <KpiCard key={b.key} label={b.label} value={summary ? summary[b.key] : '—'} accent={b.accent} Icon={b.Icon} loading={query.isFetching && !data} />
        ))}
      </div>

      <FilterBar>
        <FilterField label="Search" className="sm:col-auto">
          <div className="relative w-full">
            <SearchIcon size={11} className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5" strokeWidth={2.25} />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search" title="Search by store or item" className="!pl-5 sm:!pl-8 sm:min-w-[220px]" aria-label="Search aging" />
          </div>
        </FilterField>
        <FilterField label="Group">
          <Select value={group} onChange={(e) => setGroup(e.target.value)}>
            <option value="">All Groups</option>
            {groups.map((g) => <option key={g}>{g}</option>)}
          </Select>
        </FilterField>
        <FilterField label="Store">
          <SearchableSelect options={storeOptions} value={partyId} onChange={(v) => setPartyId(v === '' ? '' : String(v))} placeholder="Type a store name…" />
        </FilterField>
      </FilterBar>

      <Table<SisAgingRow>
        storageKey="sis-aging"
        title="Store × Item"
        count={`${total} lines`}
        rows={pageRows}
        rowKey={(r) => `${r.partyId}:${r.itemId}`}
        loading={query.isFetching && !data}
        footer={<Pagination page={page} pageSize={pageSize} total={total} setPage={setPage} setPageSize={setPageSize} />}
        columns={[
          {
            key: 'img',
            header: '',
            width: '44px',
            align: 'center',
            alwaysVisible: true,
            cell: (r) => <ItemThumb src={r.imageUrl} size={22} />,
          },
          { key: 'store', header: 'Store', cell: (r) => <span className="font-bold">{r.storeName}</span> },
          {
            key: 'item',
            header: 'Item',
            cell: (r) => `${r.itemCode} · ${r.itemName}`,
          },
          { key: 'barcode', header: 'Barcode', cell: (r) => r.itemBarcode || '—' },
          { key: 'onhand', header: 'On Hand', align: 'right', cell: (r) => <strong>{r.onHand}</strong> },
          { key: 'b1', header: '0–30', align: 'right', cell: (r) => r.d0_30 || '—' },
          { key: 'b2', header: '31–60', align: 'right', cell: (r) => r.d31_60 || '—' },
          { key: 'b3', header: '61–90', align: 'right', cell: (r) => r.d61_90 || '—' },
          {
            key: 'b4',
            header: '90+',
            align: 'right',
            cell: (r) => (r.d90plus ? <span className="font-bold text-danger">{r.d90plus}</span> : '—'),
          },
          {
            key: 'st',
            header: 'Sell-through',
            align: 'right',
            cell: (r) => `${r.sellThroughPct}%`,
          },
          { key: 'oldest', header: 'Oldest (d)', align: 'right', cell: (r) => (r.onHand > 0 ? r.oldestDays : '—') },
        ]}
      />
    </div>
  );
}
